import {
  TextField, Tooltip
} from '@material-ui/core';
import {
  createStyles, makeStyles
} from '@material-ui/core/styles';
import { Autocomplete, createFilterOptions } from "@material-ui/lab";
import { BusinessOutlined } from '@material-ui/icons';
import { observer } from 'mobx-react';
import React, { useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useUserStore } from '../../hooks/useUserStore';

interface NavigationCompanyProps {
  redirectRoute: any;
  /** 34px pill matching the inline search boxes in the crm-portal top row */
  compact?: boolean;
}

const useStyles = makeStyles(theme =>
  createStyles({
    'autoComplete': {
      width: 200,
      [theme.breakpoints.up('xl')]: {
        width: 300,
      },
      marginLeft: 16,
      '& input': {
        fontWeight: 'bold',
      },
      '& input::placeholder': {
        opacity: 1
      },
      '& input:focus::placeholder': {
        opacity: 0.5
      }
    },
    compact: {
      width: 240,
      [theme.breakpoints.up('lg')]: {
        width: 320,
      },
      marginLeft: 0,
      '& .MuiOutlinedInput-root': {
        height: 34,
        paddingTop: 0,
        paddingBottom: 0,
        paddingLeft: 12,
        fontSize: 13,
      },
      '& .MuiAutocomplete-input': {
        padding: '0 4px !important',
      },
      '& input': {
        fontWeight: 500,
      },
    },
    compactIcon: {
      fontSize: 18,
      color: theme.palette.text.secondary,
      marginRight: theme.spacing(0.5),
    },
    popper: {
      width: '80vw !important',
      maxWidth: '500px !important',
    },
    listbox: {
      '& .MuiAutocomplete-listbox': {
        width: '80vw !important',
        maxWidth: '500px !important',
      },
    },
  })
);

export interface Company {
  label: string;
  value: number;
  company: string;
}

// "Company (label)", without empty brackets when there is no label
const companyDisplayName = (company: Company) =>
  company.label ? `${company.company} (${company.label})` : company.company;

export const NavigationCompany = observer(({ redirectRoute, compact = false }: NavigationCompanyProps) => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const isCompaniesMenuOpen = Boolean(anchorEl);
  const { companyDropdownStore, userStore } = useUserStore();
  const { companies, company } = companyDropdownStore;
  const { push } = useHistory();
  const classes = useStyles();

  useEffect(() => {
    companyDropdownStore.getUserCompanies();
  }, []);

  const handleMenuItemClick = (event, value: Company | null) => {
    setAnchorEl(null);
    if (value) {
      companyDropdownStore.changeCompanyNavigation(value.value);
      companyDropdownStore.company = value;
      push(redirectRoute);
    }
  };

  const filterOptions = createFilterOptions({
    matchFrom: 'any',
    stringify: (option: Company) => [option.label, option.value, option.company].join(' '),
  });

  return (
    <Tooltip
      title={
        <h1 style={{ fontSize: 15 }}>
          {company ? companyDisplayName(company) : ''}
        </h1>
      }
      placement="left"
      arrow
    >
      <Autocomplete
        className={compact ? classes.compact : classes.autoComplete}
        classes={{
          listbox: classes.listbox,
          popper: classes.popper,
        }}
        id="menu-appbar"
        options={companies}
        size="small"
        disableClearable
        componentName="menu-appbar"
        getOptionLabel={option => companyDisplayName(option)}
        renderInput={params => (
          <TextField
            {...params}
            placeholder={company ? companyDisplayName(company) : ''}
            variant="outlined"
            InputProps={compact ? {
              ...params.InputProps,
              startAdornment: <BusinessOutlined className={classes.compactIcon} />,
            } : params.InputProps}
          />
        )}
        blurOnSelect
        onChange={handleMenuItemClick}
        value={company}
        getOptionSelected={(option, value) => option.value === value.value}
        filterOptions={filterOptions}
      />
    </Tooltip>
  );
});
